/**
 * Container Info Section
 * Booking Number (required), BL Number (optional), Container Numbers (dynamic list), and Status
 */

import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { Plus, XCircle } from '@untitledui/icons';
import type { ContainerFormData, SelectOption } from '@/hooks/useContainerForm';
import { useTranslation } from 'react-i18next';

interface ContainerInfoSectionProps {
    formData: ContainerFormData;
    onInputChange: (field: keyof ContainerFormData, value: string) => void;
    statusOptions: SelectOption[];
    containerNumbers: string[];
    onAddContainerNumber: () => void;
    onRemoveContainerNumber: (index: number) => void;
    onContainerNumberChange: (index: number, value: string) => void;
    isEditMode?: boolean;
}

export function ContainerInfoSection({
    formData,
    onInputChange,
    statusOptions,
    containerNumbers,
    onAddContainerNumber,
    onRemoveContainerNumber,
    onContainerNumberChange,
    isEditMode = false,
}: ContainerInfoSectionProps) {
    const { t } = useTranslation('pages:containers');

    return (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
            <h2 className="text-lg font-semibold text-primary mb-6">{t('form.sections.containerInfo')}</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {/* Booking Number - Primary identifier (required) */}
                <Input
                    label={t('form.fields.bookingNumber', 'Booking Number')}
                    placeholder={t('form.fields.bookingNumberPlaceholder', 'EVE2025-001234')}
                    value={formData.bookingNumber}
                    onChange={(value) => onInputChange('bookingNumber', value)}
                    size="md"
                    isRequired
                    isDisabled={isEditMode}
                    hint={isEditMode
                        ? t('form.bookingNumberReadonly', 'Booking number cannot be changed')
                        : t('form.bookingNumberHint', 'Carrier booking reference number')}
                />
                {/* BL Number (optional - needed for tracking) */}
                <Input
                    label={t('form.fields.blNumber', 'Bill of Lading')}
                    placeholder={t('form.fields.blNumberPlaceholder', 'BL123456789')}
                    value={formData.blNumber}
                    onChange={(value) => onInputChange('blNumber', value)}
                    size="md"
                    hint={t('form.blNumberHint', 'Required for carrier tracking')}
                />
                {/* Container Numbers - Dynamic list */}
                <div>
                    <div className="space-y-2">
                        {containerNumbers.map((cn, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="flex-1">
                                    <Input
                                        label={index === 0 ? t('form.fields.containerNumber', 'Container Number') : undefined}
                                        placeholder={t('form.fields.containerNumberPlaceholder', 'MAEU1234567')}
                                        value={cn}
                                        onChange={(value) => onContainerNumberChange(index, value)}
                                        size="md"
                                        hint={index === 0 && containerNumbers.length === 1
                                            ? t('form.containerNumberHint', 'ISO container number (optional - assigned by carrier)')
                                            : undefined}
                                    />
                                </div>
                                {!isEditMode && containerNumbers.length > 1 && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        color="secondary"
                                        onClick={() => onRemoveContainerNumber(index)}
                                        className={index === 0 ? 'mt-6' : ''}
                                    >
                                        <XCircle className="size-4" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                    {!isEditMode && (
                        <Button
                            type="button"
                            size="sm"
                            color="secondary"
                            iconLeading={Plus}
                            onClick={onAddContainerNumber}
                            className="mt-2"
                        >
                            {t('form.addContainer', 'Add Container')}
                        </Button>
                    )}
                </div>
                {/* Status */}
                <Select
                    label={t('form.fields.status', 'Status')}
                    placeholder={t('form.fields.statusPlaceholder', 'Select status...')}
                    items={statusOptions}
                    selectedKey={formData.status}
                    onSelectionChange={(key) => onInputChange('status', key as string)}
                    size="md"
                    isRequired
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>
        </div>
    );
}
